import React, { useContext, useEffect, useState } from 'react';

import { Check, WarningAmberRounded } from '@mui/icons-material';
import { Button, Grid, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import SideBar from '../../uiParts/SideBar';
import { response } from '../../../api/db';
import { completeChangeEmail, verifyEmail } from '../../../api/db/user';
import { gray100, primaryButtonOutlinedColor } from '../../../constants/Colors';
import { sideBarWidth, spHeaderHeight } from '../../../constants/Location';
import { AuthContext } from '../../../contexts/auth';
import { browserBack, common } from '../../../modules/error';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  container: {
    '@media screen and (min-width:961px)': {
      ml: sideBarWidth,
      height: '100vh'
    },
    '@media screen and (max-width:960px)': {
      mt: spHeaderHeight
    },
    direction: 'column',
    justifyContent: 'center',
    height: `calc(100vh - ${spHeaderHeight})`,
    minHeight: '100vh'
  },
  body: {
    '@media screen and (max-width:600px)': {
      width: '343px',
      mt: '64px'
    },
    textAlign: 'center',
    maxWidth: '560px',
    width: '95%',
    alignItems: 'center',
    mt: '160px'
  },
  icon: {
    '@media screen and (max-width:600px)': {
      height: '64px'
    },
    height: '80px',
    width: '100%',
    color: gray100
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px',
      width: '243px',
      m: '34px auto 0'
    },
    fontWeight: 500,
    color: gray100,
    fontSize: '24px',
    mt: '34px',
    textAlign: 'center'
  },
  text: {
    fontSize: '16px',
    color: gray100,
    mt: '24px',
    textAlign: 'center',
    fontWeight: 400
  },
  button: {
    maxWidth: '560px',
    width: '95%',
    mt: '80px',
    ...primaryButtonOutlinedColor
  }
};

const EmailChangeComplete: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const confirmationToken = searchParams.get('token');
  const [loading, setLoading] = useState(false);
  const [finishStatus, setfinishStatus] = useState(false);
  const [open, setOpen] = useState(false);
  const [snackbarErrorText, setSnackbarErrorText] = useState('');
  const [isChangedEmail, setIsChangedEmail] = useState(true);
  const [errorText, setErrorText] = useState(common);

  // ログインチェック
  const { isLoggedIn } = useContext(AuthContext);
  useEffect((): void => {
    if (!isLoggedIn) {
      navigate('/auth/login');
      return;
    }
  }, []);

  // ブラウザバック制御
  useEffect(() => {
    window.scrollTo(0, 0);

    void (async () => {
      setLoading(true);
      try {
        if (!confirmationToken) {
          throw new Error();
        }
        // メール認証API
        await verifyEmail(confirmationToken);
        // メールアドレス変更完了API
        await completeChangeEmail(confirmationToken);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const loginErrorResponse = axiosError.response as response;
          setErrorText(loginErrorResponse.data.message);
        }
        setIsChangedEmail(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ブラウザバック制御
  useEffect(() => {
    history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', browserBackAlert);
    return () => {
      window.removeEventListener('popstate', browserBackAlert);
    };
  }, []);

  const browserBackAlert = (e: { preventDefault: () => void }): void => {
    if (!finishStatus) {
      e.preventDefault();
      setOpen(true);
      setSnackbarErrorText(browserBack);
      window.history.pushState(null, '', window.location.pathname);
      setfinishStatus(false);
    }
  };

  return (
    <>
      <CustomHead title="メールアドレス変更完了 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid container>
        <SideBar />
        <Grid container sx={styles.container}>
          <Grid item sx={styles.body}>
            {isChangedEmail ? (
              <Check sx={styles.icon} />
            ) : (
              <WarningAmberRounded sx={styles.icon} />
            )}
            <Typography sx={styles.title}>
              {isChangedEmail
                ? 'メールアドレス変更が完了しました'
                : 'メールアドレスを変更できませんでした'}
            </Typography>
            <Typography sx={styles.text}>
              {isChangedEmail
                ? 'メールアドレスの変更が完了しました。引き続きサービスをお使いください。'
                : errorText}
            </Typography>
            <Button
              fullWidth
              onClick={() => {
                navigate('/account');
              }}
              sx={styles.button}
              variant="outlined"
            >
              アカウント管理へ
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={snackbarErrorText}
      />
    </>
  );
};

export default EmailChangeComplete;
